import { useState, useCallback, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../utils/sentryHelpers';
import {
  RAG_API_BASE_URL,
  LOCALSTORAGE_KEYS,
  ENTITY_CONFIG,
  parseExternalId,
} from './chatbotConfig';

const client = generateClient();

function loadFromStorage(key, fallback) {
  try {
    const val = localStorage.getItem(key);
    return val !== null ? val : fallback;
  } catch {
    return fallback;
  }
}

export default function useChatbot() {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNamespace, setSelectedNamespaceState] = useState(
    () => loadFromStorage(LOCALSTORAGE_KEYS.selectedNamespace, 'federation')
  );
  const [namespaceKeys, setNamespaceKeysState] = useState(() => ({
    public: loadFromStorage(LOCALSTORAGE_KEYS.public, ''),
    premium: loadFromStorage(LOCALSTORAGE_KEYS.premium, ''),
    federation: loadFromStorage(LOCALSTORAGE_KEYS.federation, 'fed_8920a5688804f4236c3513ffbd30aeaed57a12150f215e91557c948b8f820724'),
  }));
  const [maxSources, setMaxSourcesState] = useState(
    () => parseInt(loadFromStorage(LOCALSTORAGE_KEYS.maxSources, '5'), 10)
  );

  // Entity cache to avoid re-fetching
  const entityCache = useRef(new Map());

  // --- Setters with localStorage persistence ---

  const setSelectedNamespace = useCallback((ns) => {
    setSelectedNamespaceState(ns);
    try { localStorage.setItem(LOCALSTORAGE_KEYS.selectedNamespace, ns); } catch {}
  }, []);

  const setNamespaceKey = useCallback((ns, key) => {
    setNamespaceKeysState((prev) => ({ ...prev, [ns]: key }));
    try { localStorage.setItem(LOCALSTORAGE_KEYS[ns], key); } catch {}
  }, []);

  const setMaxSources = useCallback((val) => {
    const n = Math.max(1, Math.min(20, parseInt(val, 10) || 5));
    setMaxSourcesState(n);
    try { localStorage.setItem(LOCALSTORAGE_KEYS.maxSources, String(n)); } catch {}
  }, []);

  // --- Entity resolution ---

  const resolveEntity = useCallback(async (entityType, id) => {
    const cacheKey = `${entityType}_${id}`;
    if (entityCache.current.has(cacheKey)) {
      return entityCache.current.get(cacheKey);
    }

    const config = ENTITY_CONFIG[entityType];
    if (!config) return null;

    try {
      const { data } = await client.graphql({
        query: config.query,
        variables: { id },
      });
      const entity = data[config.queryName] || null;
      entityCache.current.set(cacheKey, entity);
      return entity;
    } catch (err) {
      console.warn(`Failed to resolve ${entityType} ${id}:`, err.message);
      entityCache.current.set(cacheKey, null);
      return null;
    }
  }, []);

  const resolveSourceEntities = useCallback(async (sources, messageIndex) => {
    if (!sources || typeof sources !== 'object') return;

    const entries = Object.entries(sources);
    const resolved = await Promise.all(
      entries.map(async ([key, source]) => {
        const parsed = parseExternalId(source.externalDocumentId);
        if (!parsed) {
          return {
            sourceIndex: key,
            externalDocumentId: source.externalDocumentId,
            entityType: null,
            subType: null,
            entity: null,
            score: source.score,
          };
        }

        const entity = await resolveEntity(parsed.entityType, parsed.id);
        return {
          sourceIndex: key,
          externalDocumentId: source.externalDocumentId,
          entityType: parsed.entityType,
          subType: parsed.subType,
          entity,
          score: source.score,
        };
      })
    );

    // Update the message with resolved sources
    setMessages((prev) =>
      prev.map((msg, idx) =>
        idx === messageIndex ? { ...msg, resolvedSources: resolved } : msg
      )
    );
  }, [resolveEntity]);

  // --- Send query ---

  const sendQuery = useCallback(async (queryText) => {
    const trimmed = queryText.trim();
    if (!trimmed) return;

    const apiKey = namespaceKeys[selectedNamespace];
    if (!apiKey) {
      setError(`Kein API-Key für "${selectedNamespace}" hinterlegt. Bitte in den Settings eintragen.`);
      return;
    }

    setError(null);

    // Build conversation history from previous messages (user + assistant only)
    const conversationHistory = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }));

    // Add user message
    const userMsg = { role: 'user', content: trimmed, timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const res = await fetch(`${RAG_API_BASE_URL}/api/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
        },
        body: JSON.stringify({
          query: trimmed,
          maxSources: maxSources,
          conversationHistory,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API Error ${res.status}: ${text}`);
      }

      const data = await res.json();

      const assistantMsg = {
        role: 'assistant',
        content: data.answer || 'Keine Antwort erhalten.',
        sources: data.sources || {},
        resolvedSources: null,
        metadata: data.metadata || {},
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const newMessages = [...prev, assistantMsg];
        // Trigger entity resolution for the new message
        const msgIndex = newMessages.length - 1;
        resolveSourceEntities(data.sources, msgIndex);
        return newMessages;
      });
    } catch (err) {
      const errorMsg = {
        role: 'error',
        content: err.message,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
      captureError(err, {
        feature: 'rag-chatbot',
        operation: 'query',
        context: { query: trimmed, namespace: selectedNamespace },
      });
    } finally {
      setIsLoading(false);
    }
  }, [namespaceKeys, selectedNamespace, maxSources, messages, resolveSourceEntities]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    selectedNamespace,
    setSelectedNamespace,
    namespaceKeys,
    setNamespaceKey,
    maxSources,
    setMaxSources,
    sendQuery,
    clearChat,
  };
}
